import { Helmet } from "react-helmet-async";
import {
  MapPin,
  Clock,
  Building2,
  Navigation,
  Check,
  ArrowRight,
  Star,
} from "lucide-react";

const SectionLabel = ({ children }) => (
  <p className="font-heading text-[10px] font-bold uppercase tracking-[0.2em] text-primary mb-2">
    {children}
  </p>
);

const formatPrice = (price, currency) => {
  if (!price) return "—";
  const symbols = { CAD: "C$", USD: "$", EUR: "€", GBP: "£" };
  return `${symbols[currency] || currency || ""}${parseFloat(price).toFixed(2)}`;
};

const CarCard = ({ car }) => (
  <div className="group bg-surface rounded-xl border border-surface-border overflow-hidden hover:shadow-card hover:border-primary/30 transition-all">
    <div className="relative aspect-[16/9] overflow-hidden flex items-center justify-center p-4">
      <img
        src={car.image}
        alt={car.name}
        className="max-w-full max-h-28 object-contain scale-110 group-hover:scale-125 transition-transform duration-500"
        onError={(e) => {
          e.target.style.opacity = "0.3";
        }}
      />
      <span className="absolute top-3 left-3 bg-text text-white px-3 py-1 rounded-full font-heading text-[10px] font-semibold uppercase tracking-wider">
        {car.type}
      </span>
    </div>
    <div className="p-5">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div>
          <h3 className="font-heading font-semibold text-text text-sm">
            {car.name}
          </h3>
          <p className="font-body text-xs text-text-muted">or similar class</p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="font-heading font-bold text-text text-base">
            {formatPrice(car.price, car.currency)}
          </p>
          <p className="font-body text-[10px] text-text-muted uppercase tracking-wide">
            / day
          </p>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 mb-4">
        {Object.entries(car.specs || {}).map(([key, val]) => (
          <span
            key={key}
            className="font-body text-[10px] bg-surface-raised text-text-muted px-2 py-1 rounded-md border border-surface-border"
          >
            {val} {key}
          </span>
        ))}
      </div>
      <a
        href="/"
        className="block w-full py-2.5 text-center rounded-lg bg-primary hover:bg-primary-hover text-white font-heading text-xs font-semibold uppercase tracking-wider transition-colors duration-200"
      >
        Book Now
      </a>
    </div>
  </div>
);

const LocationView = ({ data }) => {
  if (!data || !data.location) return null;

  const { location, featuredCars, seo } = data;
  const { pickupInstructions } = location;

  const directionsUrl =
    location?.coordinates?.lat && location?.coordinates?.lng
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
          `${location.name} ${location.coordinates.lat},${location.coordinates.lng}`,
        )}`
      : location?.mapData?.googlePlaceId
        ? `https://www.google.com/maps/search/?api=1&query_place_id=${encodeURIComponent(location.mapData.googlePlaceId)}&query=${encodeURIComponent(location.name)}`
        : null;

  const mapEmbedUrl =
    location?.coordinates?.lat && location?.coordinates?.lng
      ? `https://maps.google.com/maps?q=${location.coordinates.lat},${location.coordinates.lng}&z=15&output=embed`
      : null;

  return (
    <div className="min-h-screen font-body mt-12">
      <Helmet>
        <title>{seo?.title || `${location.name} Car Rental | RentGurus`}</title>
        {seo?.metaDescription && (
          <meta name="description" content={seo.metaDescription} />
        )}
        {seo?.canonicalUrl && <link rel="canonical" href={seo.canonicalUrl} />}
        {seo?.structuredData && (
          <script type="application/ld+json">
            {JSON.stringify(seo.structuredData)}
          </script>
        )}
      </Helmet>

      {/* ── HERO ── */}
      <header
        className="px-4 sm:px-6 pt-10 pb-14 relative overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, #14222d 0%, #14222d 45%, #405759 60%, #587471 78%, #90bda8 100%)",
        }}
      >
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff06_1px,transparent_1px),linear-gradient(to_bottom,#ffffff06_1px,transparent_1px)] bg-[size:32px_32px] pointer-events-none" />
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="inline-flex items-center gap-2 border border-primary/30 bg-primary/10 px-3 py-1.5 rounded-full mb-5">
            <span className="w-2 h-2 rounded-full bg-primary" />
            <span className="font-heading text-[11px] font-semibold text-primary uppercase tracking-widest">
              Airport Location
            </span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-3">
            {location.name}
          </h1>
          <div className="flex items-center gap-2 text-white/60 font-body text-sm">
            <MapPin size={14} className="flex-shrink-0 text-primary" />
            <span>{location.address}</span>
          </div>
        </div>
      </header>

      {/* ── MAIN CONTENT ── */}
      <div className="px-4 sm:px-6 py-10">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-8 items-start">
          {/* ── LEFT COLUMN ── */}
          <div className="lg:col-span-8 space-y-8">
            {/* Map + Pickup Instructions */}
            <div className="bg-surface rounded-xl border border-surface-border overflow-hidden shadow-card">
              {mapEmbedUrl ? (
                <iframe
                  title={`Map of ${location.name}`}
                  width="100%"
                  height="240"
                  loading="lazy"
                  style={{ border: 0, display: "block" }}
                  src={mapEmbedUrl}
                />
              ) : location?.mapData?.staticMapUrl ? (
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={location.mapData.staticMapUrl}
                    alt={`Map of ${location.name}`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="bg-surface px-5 py-2.5 rounded-full shadow-card flex items-center gap-2 border border-surface-border">
                      <span className="w-2.5 h-2.5 rounded-full bg-destructive animate-pulse" />
                      <span className="font-heading font-semibold text-text text-sm">
                        {location.iataCode || "Airport"} Terminal
                      </span>
                    </div>
                  </div>
                </div>
              ) : null}

              {/* Pickup instructions */}
              <div className="p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-9 h-9 rounded-lg bg-accent flex items-center justify-center flex-shrink-0">
                    <Navigation size={16} className="text-accent-foreground" />
                  </div>
                  <h2 className="font-heading text-lg font-bold text-text">
                    Pickup Instructions
                  </h2>
                </div>

                {pickupInstructions?.summary && (
                  <p className="font-body text-text-muted text-sm leading-relaxed mb-6 p-4 bg-surface-raised rounded-lg border border-surface-border">
                    {pickupInstructions.summary}
                  </p>
                )}

                {pickupInstructions?.steps?.length > 0 && (
                  <div className="space-y-5 relative before:absolute before:left-[13px] before:top-5 before:bottom-5 before:w-px before:bg-surface-border">
                    {pickupInstructions.steps.map((s) => (
                      <div
                        key={s.step}
                        className="relative pl-10 flex items-start"
                      >
                        <div className="absolute left-0 top-0 w-7 h-7 rounded-full bg-dark-light text-white font-heading font-bold text-xs flex items-center justify-center z-10 flex-shrink-0">
                          {s.step}
                        </div>
                        <p className="font-body text-text-muted text-sm leading-relaxed pt-1">
                          {s.text}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Featured cars */}
            {featuredCars?.length > 0 && (
              <section>
                <SectionLabel>Available Here</SectionLabel>
                <div className="flex items-center justify-between mb-5">
                  <h2 className="font-heading text-xl font-bold text-text">
                    Available at this Location
                  </h2>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  {featuredCars.map((car) => (
                    <CarCard key={car.id} car={car} />
                  ))}
                </div>
              </section>
            )}
          </div>

          {/* ── RIGHT SIDEBAR ── */}
          <aside className="lg:col-span-4 space-y-4 lg:sticky lg:top-24">
            {/* Location details */}
            <div className="bg-dark-light rounded-xl p-6 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 rounded-full blur-3xl pointer-events-none" />
              <h3 className="font-heading text-sm font-bold text-white mb-5 relative z-10">
                Location Details
              </h3>

              <div className="space-y-4 relative z-10">
                <div className="flex gap-3">
                  <MapPin
                    size={16}
                    className="text-primary flex-shrink-0 mt-0.5"
                  />
                  <div>
                    <p className="font-heading text-[10px] font-bold text-white/40 uppercase tracking-widest mb-0.5">
                      Address
                    </p>
                    <p className="font-body text-white/80 text-xs leading-relaxed">
                      {location.address}
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Clock
                    size={16}
                    className="text-primary flex-shrink-0 mt-0.5"
                  />
                  <div>
                    <p className="font-heading text-[10px] font-bold text-white/40 uppercase tracking-widest mb-0.5">
                      Hours
                    </p>
                    <p className="font-body text-white/80 text-xs">
                      {location.hours}
                    </p>
                  </div>
                </div>

                {location.facility && (
                  <div className="flex gap-3">
                    <Building2
                      size={16}
                      className="text-primary flex-shrink-0 mt-0.5"
                    />
                    <div>
                      <p className="font-heading text-[10px] font-bold text-white/40 uppercase tracking-widest mb-0.5">
                        Facility
                      </p>
                      <p className="font-body text-white/80 text-xs">
                        {location.facility}
                      </p>
                    </div>
                  </div>
                )}

                {location.rating && (
                  <div className="flex gap-3">
                    <Star
                      size={16}
                      className="text-primary fill-primary flex-shrink-0 mt-0.5"
                    />
                    <div>
                      <p className="font-heading text-[10px] font-bold text-white/40 uppercase tracking-widest mb-0.5">
                        Rating
                      </p>
                      <p className="font-body text-white/80 text-xs">
                        {location.rating}/5.0 ·{" "}
                        {location.reviewCount?.toLocaleString()} reviews
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {directionsUrl && (
                <a
                  href={directionsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 w-full py-3 flex items-center justify-center gap-2 bg-primary hover:bg-primary-hover text-white font-heading text-xs font-bold uppercase tracking-widest rounded-lg transition-colors relative z-10"
                >
                  <Navigation size={13} />{" "}
                  {location.cta?.secondaryText || "Get Directions"}
                </a>
              )}
            </div>

            {/* Why rent here */}
            {location.highlights?.length > 0 && (
              <div className="bg-surface rounded-xl border border-surface-border p-6 shadow-card">
                <h3 className="font-heading text-sm font-bold text-text mb-4">
                  Why rent here?
                </h3>
                <ul className="space-y-3">
                  {location.highlights.map((h, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <div className="w-5 h-5 rounded-full bg-accent flex items-center justify-center flex-shrink-0">
                        <Check size={11} className="text-accent-foreground" />
                      </div>
                      <span className="font-body text-sm text-text-muted">
                        {h}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
};

export default LocationView;
